import type {
  IFilterOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  ToeLinesFiltersInput,
  ToeLinesLanguageSupportEnum,
  ToeLinesNeedsReviewEnum,
} from "gql/graphql";
import type { IToeLinesData } from "pages/group/surface/lines/types";
import { useFiltersStorage } from "storage/filters";
import {
  getCheckedFilter,
  getDateRangeFilter,
  getNewFilter,
  getNumberRangeFilter,
} from "utils/filters";
import { formatBoolean, formatStatus } from "utils/format-helpers";

const useToELinesFilters = ({
  groupName,
  setBackFilters,
  isInternal,
}: {
  groupName: string;
  setBackFilters: React.Dispatch<React.SetStateAction<ToeLinesFiltersInput>>;
  isInternal: boolean;
}): IUseFilterProps<IToeLinesData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const options: IOptionsProps<IToeLinesData>[] =
    useMemo((): IOptionsProps<IToeLinesData>[] => {
      const baseOptions: IOptionsProps<IToeLinesData>[] = [
        {
          filterOptions: [
            {
              filterFn: "includesInsensitive",
              key: "filename",
              label: t("group.toe.lines.filename"),
              type: "text",
            },
          ],
          label: t("group.toe.lines.filename"),
        },
        {
          filterOptions: [
            {
              key: "loc",
              label: t("group.toe.lines.loc"),
              type: "numberRange",
            },
          ],
          label: t("group.toe.lines.loc"),
        },
        {
          filterOptions: [
            {
              key: "hasVulnerabilities",
              label: t("group.toe.lines.status"),
              options: [
                { label: formatStatus(true), value: "true" },
                { label: formatStatus(false), value: "false" },
              ],
              type: "radioButton",
            },
          ],
          label: t("group.toe.lines.status"),
        },
        {
          filterOptions: [
            {
              key: "modifiedDate",
              label: t("group.toe.lines.modifiedDate"),
              type: "dateRange",
            },
          ],
          label: t("group.toe.lines.modifiedDate"),
        },
        {
          filterOptions: [
            {
              key: "lastCommit",
              label: t("group.toe.lines.lastCommit"),
              type: "text",
            },
          ],
          label: t("group.toe.lines.lastCommit"),
        },
        {
          filterOptions: [
            {
              key: "lastAuthor",
              label: t("group.toe.lines.lastAuthor"),
              type: "text",
            },
          ],
          label: t("group.toe.lines.lastAuthor"),
        },
        {
          filterOptions: [
            {
              key: "seenAt",
              label: t("group.toe.lines.seenAt"),
              type: "dateRange",
            },
          ],
          label: t("group.toe.lines.seenAt"),
        },
        {
          filterOptions: [
            {
              key: "sortsPriorityFactor",
              label: t("group.toe.lines.sortsPriorityFactor"),
              type: "numberRange",
            },
          ],
          label: t("group.toe.lines.sortsPriorityFactor"),
        },
        {
          filterOptions: [
            {
              key: "bePresent",
              label: t("group.toe.lines.bePresent"),
              options: [
                { label: formatBoolean(true), value: "true" },
                { label: formatBoolean(false), value: "false" },
              ],
              type: "radioButton",
            },
          ],
          label: t("group.toe.lines.bePresent"),
        },
      ];

      const conditionalOptions: IOptionsProps<IToeLinesData>[] = isInternal
        ? [
            {
              filterOptions: [
                {
                  key: "languageSupport",
                  label: t("group.toe.lines.language"),
                  options: [
                    { label: "Supported", value: "SUPPORTED" },
                    { label: "Not Supported", value: "UNSUPPORTED" },
                  ],
                  type: "radioButton",
                },
              ],
              label: t("group.toe.lines.language"),
            },
            {
              filterOptions: [
                {
                  key: "needsReview",
                  label: t("group.toe.lines.needsReview"),
                  options: [
                    { label: "Yes", value: "YES" },
                    { label: "No", value: "NO" },
                  ],
                  type: "radioButton",
                },
              ],
              label: t("group.toe.lines.needsReview"),
            },
          ]
        : [];

      return baseOptions.concat(conditionalOptions);
    }, [t, isInternal]);

  const handleGetValues = useCallback((): string | null => {
    return getValues("toe_lines", groupName) as string | null;
  }, [getValues, groupName]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("toe_lines", value, groupName);
    },
    [setValues, groupName],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IToeLinesData>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IToeLinesData>[];
      const filenameValue = getNewFilter(newFiltersArray, "filename")?.value;
      const { min: minLocValue, max: maxLocValue } = getNumberRangeFilter(
        newFiltersArray,
        "loc",
      );
      const hasVulnerabilitiesValue = getCheckedFilter(
        newFiltersArray,
        "hasVulnerabilities",
      )?.value;
      const needsReviewValue = getCheckedFilter(newFiltersArray, "needsReview")
        ?.value as ToeLinesNeedsReviewEnum | undefined;
      const languageSupportValue = getCheckedFilter(
        newFiltersArray,
        "languageSupport",
      )?.value as ToeLinesLanguageSupportEnum | undefined;
      const { from: fromModDateValue, to: toModDateValue } = getDateRangeFilter(
        newFiltersArray,
        "modifiedDate",
      );
      const lastCommitValue = getNewFilter(
        newFiltersArray,
        "lastCommit",
      )?.value;
      const lastAuthorValue = getNewFilter(
        newFiltersArray,
        "lastAuthor",
      )?.value;
      const { from: fromSeenAtValue, to: toSeenAtValue } = getDateRangeFilter(
        newFiltersArray,
        "seenAt",
      );
      const { min: minSortsValue, max: maxSortsValue } = getNumberRangeFilter(
        newFiltersArray,
        "sortsPriorityFactor",
      );
      const bePresentValue = getCheckedFilter(
        newFiltersArray,
        "bePresent",
      )?.value;
      const toeLinesBackFilters = {
        bePresent:
          bePresentValue === undefined ? undefined : bePresentValue === "true",
        filename: filenameValue ?? undefined,
        fromModifiedDate: fromModDateValue ?? undefined,
        fromSeenAt: fromSeenAtValue ?? undefined,
        hasVulnerabilities:
          hasVulnerabilitiesValue === undefined
            ? undefined
            : hasVulnerabilitiesValue === "true",
        languageSupport: languageSupportValue ?? undefined,
        lastAuthor: lastAuthorValue ?? undefined,
        lastCommit: lastCommitValue ?? undefined,
        maxLoc: maxLocValue ?? undefined,
        maxSortsPriorityFactor: maxSortsValue ?? undefined,
        minLoc: minLocValue ?? undefined,
        minSortsPriorityFactor: minSortsValue ?? undefined,
        needsReview: needsReviewValue ?? undefined,
        toModifiedDate: toModDateValue ?? undefined,
        toSeenAt: toSeenAtValue ?? undefined,
      };

      setBackFilters(toeLinesBackFilters);
    },
    [setBackFilters],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { useToELinesFilters };
